import { useEffect, useCallback } from "react";
import { useDispatch, useSelector } from "react-redux";
import { showModalPopup, showToastNotification } from "../../../App/state/actions";
import { getRecordingAction } from "../../RecordingsPage/state/actions";
import { isRecordingPendingStateCheck } from "../../../shared/utils/recordingHelper";
import { ANALYTICS_STORAGE_KEYS, LINK_INFO } from "../../../shared/constants/analytics";
import constants from "../../../shared/constants";
import recordingConstants from "../../../shared/constants/recordingConstants";
const { RECORDING_PARAMS, MR_RECORDING_FILTER_OPTIONS } = recordingConstants;
import { setSessionStorage } from "../../../shared/utils/sessionStorage";
import {
  getRecordingInfo,
  navigateToPVRManager,
  showCPVRConflictModal,
  checkCPVRConflicts,
} from "../../../shared/utils/recordingHelper";
import { useTranslation } from "react-i18next";

const { MODAL_TYPES } = constants;

const useRecordingEffects = ({
  isRecordingActionInProgress,
  isMR,
  setRecordingActionError,
  resetRecordingActionValues,
  selectedAssetRecordingInfo,
  appProvider,
  setSelectedAssetRecordingInfo,
  setShowRecordingModal,
  displayRecordingToast,
  recordingActionError,
  isTeamRecordingsEnabled,
}) => {
  const manipulatedRecordingParams = useSelector(({ recording }) => recording.manipulatedRecordingParams);
  const deleteRecordingResponse = useSelector(({ recording }) => recording.deleteRecordingResponse);
  const recordingsList = useSelector(({ recording }) => recording.recordingsList);
  const getRecordingError = useSelector(({ recording }) => recording.getRecordingError);
  const setRecordingResponse = useSelector(({ recording }) => recording.setRecordingResponse);
  const editRecordingResponse = useSelector(({ recording }) => recording.editRecordingResponse);
  const setTeamRecordingResponse = useSelector(({ recording }) => recording.setTeamRecordingResponse);
  const dispatch = useDispatch();
  const { t: translate } = useTranslation();
  const fetchRecordings = useCallback(
    (recordingParamsMR = null, fetchingAfterAction = false) => {
      isRecordingActionInProgress.current = fetchingAfterAction;
      if (isMR) {
        dispatch(
          getRecordingAction(
            appProvider,
            true,
            null,
            null,
            null,
            MR_RECORDING_FILTER_OPTIONS.SCHEDULED_PAGE_FILTERS,
            recordingParamsMR
          )
        );
      } else {
        dispatch(getRecordingAction(appProvider, false, true, null, [RECORDING_PARAMS.EVENT, RECORDING_PARAMS.SERIES]));
      }
    },
    [appProvider, dispatch, isMR, isRecordingActionInProgress]
  );
  /**
   * This useEffect is used after we have attempted to schedule a recording
   */
  useEffect(() => {
    if (setRecordingResponse && isRecordingActionInProgress.current === false) {
      if (isMR) {
        if (setRecordingResponse.code) {
          // Error occurred
          setRecordingActionError(setRecordingResponse);
        } else {
          fetchRecordings(
            isRecordingPendingStateCheck(setRecordingResponse.content) ? setRecordingResponse.recordingParams : null,
            true
          );
        }
      } else {
        if (setRecordingResponse.code) {
          // Error occurred
          if (checkCPVRConflicts(setRecordingResponse)) {
            // show popup modal for recording conflict error
            showCPVRConflictModal(
              () => {
                navigateToPVRManager(true); // Redirect user to PVR manager scheduled page to manage the conflict
                setSessionStorage(
                  ANALYTICS_STORAGE_KEYS.LINK,
                  `${LINK_INFO.MANAGE_RECORDINGS};${LINK_INFO.RECORDING_PROMPT}`
                );
              },
              () => {
                dispatch(showToastNotification(translate("recording_not_set")));
              },
              MODAL_TYPES.ERROR,
              showModalPopup,
              dispatch
            );
            resetRecordingActionValues();
          } else {
            // show generic 'not set' toast notification for all other errors
            setRecordingActionError(setRecordingResponse);
          }
        } else {
          // No error in the response, refresh our recordings list
          fetchRecordings(null, true);
        }
      }
    }
  }, [
    setRecordingResponse,
    isMR,
    fetchRecordings,
    dispatch,
    resetRecordingActionValues,
    isRecordingActionInProgress,
    setRecordingActionError,
    translate,
  ]);

  /**
   * This useEffect is used after we have attempted to schedule a team recording
   */
  useEffect(() => {
    if (setTeamRecordingResponse && isRecordingActionInProgress.current === false) {
      if (setTeamRecordingResponse.code) {
        // Error occurred
        if (checkCPVRConflicts(setTeamRecordingResponse)) {
          // show popup modal for recording conflict error
          showCPVRConflictModal(
            () => {
              navigateToPVRManager(true); // Redirect user to PVR manager scheduled page to manage the conflict
              setSessionStorage(
                ANALYTICS_STORAGE_KEYS.LINK,
                `${LINK_INFO.MANAGE_RECORDINGS};${LINK_INFO.RECORDING_PROMPT}`
              );
            },
            () => {
              //It is yet to be decided what needs to be displayed when there is an error on the team recording action.
              dispatch(showToastNotification(RECORDING_PARAMS.TEAM_RECORDING_NOT_SET));
            },
            MODAL_TYPES.ERROR,
            showModalPopup,
            dispatch
          );
          resetRecordingActionValues();
        } else {
          // show generic 'not set' toast notification for all other errors
          setRecordingActionError(setTeamRecordingResponse);
        }
      } else {
        // No error in the response, refresh our recordings list
        fetchRecordings(null, true);
      }
    }
  }, [
    fetchRecordings,
    dispatch,
    resetRecordingActionValues,
    isRecordingActionInProgress,
    setRecordingActionError,
    translate,
    setTeamRecordingResponse,
    isTeamRecordingsEnabled,
  ]);

  /**
   * This useEffect is used after we have attempted to cancel a recording
   */
  useEffect(() => {
    if (deleteRecordingResponse && isRecordingActionInProgress.current === false) {
      if (deleteRecordingResponse.code) {
        // Error occurred
        setRecordingActionError(deleteRecordingResponse);
      } else {
        // Nothing useful returned in the MR delete response, so we assume its pending
        fetchRecordings(isMR ? deleteRecordingResponse.recordingParams : null, true);
      }
    }
  }, [deleteRecordingResponse, fetchRecordings, isMR, isRecordingActionInProgress, setRecordingActionError]);

  /**
   * This useEffect is used after we have re-fetched recordings following a set/cancel recording action.
   */
  useEffect(() => {
    if (selectedAssetRecordingInfo && manipulatedRecordingParams && isRecordingActionInProgress.current) {
      // Can't use the selectedAssetRecordingInfo as-is, need to update it with the latest recording state checks/values
      const selectedRecordingInfo = getRecordingInfo(
        isMR,
        selectedAssetRecordingInfo.assetToRecord,
        recordingsList,
        appProvider?.channelMapID
      );
      setSelectedAssetRecordingInfo(selectedRecordingInfo);

      if (isMR && selectedRecordingInfo.recordingEventConflictCheck) {
        setShowRecordingModal(true);
      } else {
        displayRecordingToast(selectedRecordingInfo, manipulatedRecordingParams);
      }
      resetRecordingActionValues();
    }
  }, [
    selectedAssetRecordingInfo,
    isMR,
    displayRecordingToast,
    manipulatedRecordingParams,
    resetRecordingActionValues,
    recordingsList,
    appProvider,
    isRecordingActionInProgress,
    setSelectedAssetRecordingInfo,
    setShowRecordingModal,
  ]);

  /**
   * This useEffect is used to deal with errors that occur during recording CRUD actions
   */
  useEffect(() => {
    if ((getRecordingError || recordingActionError) && manipulatedRecordingParams) {
      displayRecordingToast(getRecordingError || recordingActionError, manipulatedRecordingParams);
      resetRecordingActionValues();
    }
  }, [
    displayRecordingToast,
    getRecordingError,
    manipulatedRecordingParams,
    recordingActionError,
    resetRecordingActionValues,
  ]);

  /**
   * This useEffect is used after we have attempted to edit a recording
   */
  useEffect(() => {
    if (editRecordingResponse && isRecordingActionInProgress.current === false) {
      if (editRecordingResponse.code) {
        // Error occurred
        setRecordingActionError(editRecordingResponse);
      } else {
        fetchRecordings(
          isMR && isRecordingPendingStateCheck(editRecordingResponse.content)
            ? editRecordingResponse.recordingParams
            : null,
          true
        );
      }
    }
  }, [editRecordingResponse, isMR, fetchRecordings, isRecordingActionInProgress, setRecordingActionError]);
};

export default useRecordingEffects;
